import type { Href } from "expo-router";
import { router, useLocalSearchParams } from "expo-router";
import { useEffect, useState } from "react";
import { ActivityIndicator, Linking, Platform, ScrollView, Text, View } from "react-native";

import { ShortlistButton } from "../../../../components/ShortlistButton";
import { Badge, Card, EmptyState, Section } from "../../../../components/ui";
import { COLORS, FONTS } from "../../../../lib/brand";
import { PANE_THEMES } from "../../../../components/brand/paneThemes";
import { getClientCompetition } from "../../../../lib/clientCompetitions";
import {
  getCompetitionLeaderboard,
  getTeamMembers,
  getTeamScores,
  listCompetitionSubmissions,
} from "../../../../lib/leaderboard";
import { getSubmissionDownloadUrl } from "../../../../lib/submissions";
import type {
  ClientCompetitionListRow,
  CompetitionSubmissionRow,
  LeaderboardRow,
  TeamMemberProfileRow,
  TeamScoreBreakdown,
} from "../../../../types/database";

const THEME = PANE_THEMES.Client;

function formatScore(v: number | null): string {
  if (v == null) return "—";
  return `${v.toFixed(2)} / 10`;
}

async function openInNewTab(url: string) {
  if (Platform.OS === "web") {
    window.open(url, "_blank", "noopener,noreferrer");
  } else {
    await Linking.openURL(url);
  }
}

export default function ClientTeamDetailScreen() {
  const { id: teamId, competitionId } = useLocalSearchParams<{ id: string; competitionId?: string }>();
  const [leaderboardRow, setLeaderboardRow] = useState<LeaderboardRow | null>(null);
  const [members, setMembers] = useState<TeamMemberProfileRow[]>([]);
  const [scores, setScores] = useState<TeamScoreBreakdown[]>([]);
  const [submissions, setSubmissions] = useState<CompetitionSubmissionRow[]>([]);
  const [comp, setComp] = useState<ClientCompetitionListRow | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      if (typeof teamId !== "string") return;
      setLoading(true);
      const [memberRows, scoreRows] = await Promise.all([getTeamMembers(teamId), getTeamScores(teamId)]);
      setMembers(memberRows);
      setScores(scoreRows);

      if (typeof competitionId === "string") {
        const [board, allSubs, c] = await Promise.all([
          getCompetitionLeaderboard(competitionId),
          listCompetitionSubmissions(competitionId),
          getClientCompetition(competitionId),
        ]);
        setLeaderboardRow(board.find((r) => r.team_id === teamId) ?? null);
        setSubmissions(allSubs.filter((s) => s.team_id === teamId));
        setComp(c);
      } else {
        setLeaderboardRow(null);
        setSubmissions([]);
        setComp(null);
      }
      setLoading(false);
    })();
  }, [teamId, competitionId]);

  if (loading) {
    return (
      <View style={{ flex: 1, alignItems: "center", justifyContent: "center", paddingVertical: 80 }}>
        <ActivityIndicator size="large" color={THEME.accent} />
      </View>
    );
  }

  return (
    <ScrollView className="flex-1 px-4 pt-4" contentContainerStyle={{ paddingBottom: 32 }}>
        <Section
          overline="Team detail"
          title={leaderboardRow?.team_name ?? "Team"}
          subtitle={`${members.length} members`}
        >
          <View className="flex-row flex-wrap gap-2">
            <Badge status="grey">Placement {leaderboardRow?.placement != null ? `#${leaderboardRow.placement}` : "Pending"}</Badge>
            <Badge status="info">Final {formatScore(leaderboardRow?.final_score ?? null)}</Badge>
          </View>
        </Section>

        <Text className="mt-6 text-lg font-semibold text-white">Members</Text>
        <View className="mt-2">
          {members.map((m) => (
            <Card
              key={m.student_id}
              variant="dark"
              padding="md"
              onPress={() => {
                if (typeof competitionId === "string") {
                  router.push(
                    `/client/student/${m.student_id}?competitionId=${encodeURIComponent(competitionId)}` as Href
                  );
                  return;
                }
                router.push(`/client/student/${m.student_id}` as Href);
              }}
            >
              <Text className="text-base font-medium text-white">{m.full_name ?? "Student"}</Text>
              <Text className="mt-0.5 text-sm text-white/70">{m.university ?? "—"}</Text>
              {typeof competitionId === "string" ? (
                <View className="mt-2 self-start">
                  <ShortlistButton competitionId={competitionId} studentId={m.student_id} />
                </View>
              ) : null}
            </Card>
          ))}
        </View>

        <Text className="mt-6 text-lg font-semibold text-white">Per-deliverable scores</Text>
        {scores.length === 0 ? (
          <Card variant="dark" padding="md"><EmptyState title="No score data yet" /></Card>
        ) : (
          <View className="mt-2">
            {scores.map((d) => (
              <Card key={`${d.deliverable_sequence}-${d.deliverable_name}`} variant="dark" padding="md">
                <Text className="text-sm font-semibold text-white">
                  {d.deliverable_sequence}. {d.deliverable_name}
                </Text>
                <Text className="mt-1 text-xs text-white/70">
                  Avg: {formatScore(d.avg_score)} · {d.judge_scores.length} judge{d.judge_scores.length === 1 ? "" : "s"}
                </Text>
                <Text className="mt-1 text-xs text-white/60">
                  {d.is_locked_gate ? "Locked gate" : "Standard"} · {d.point_value} pts
                </Text>
                {(() => {
                  const sub = submissions.find(
                    (s) =>
                      s.deliverable_sequence === d.deliverable_sequence &&
                      s.deliverable_name === d.deliverable_name
                  );
                  if (!sub) return null;
                  return (
                    <Text
                      className="mt-1 text-xs text-amber underline"
                      onPress={async () => {
                        const { url, error } = await getSubmissionDownloadUrl(sub.file_url);
                        if (error || !url) return;
                        await openInNewTab(url);
                      }}
                    >
                      Submission: {sub.file_name}
                    </Text>
                  );
                })()}
                {d.judge_scores.length === 0 ? (
                  <View className="mt-2 self-start"><Badge status="grey">Pending</Badge></View>
                ) : (
                  <View className="mt-2">
                    {d.judge_scores.map((s, idx) => (
                      <Card key={idx} variant="dark" padding="sm">
                        <Text className="text-xs text-white/80">
                          Score {s.score ?? "—"}
                          {s.passed_gate != null ? ` · ${s.passed_gate ? "Pass" : "Fail"}` : ""}
                        </Text>
                        {s.category_scores.some((c) => c.sub_score != null) ? (
                          <Text className="mt-0.5 text-xs text-white/55">
                            {s.category_scores
                              .filter((c) => c.sub_score != null)
                              .map((c) => `${c.name} ${c.sub_score}`)
                              .join(" · ")}
                          </Text>
                        ) : null}
                        {s.rubric_notes ? <Text className="mt-0.5 text-xs text-white/70">{s.rubric_notes}</Text> : null}
                      </Card>
                    ))}
                  </View>
                )}
              </Card>
            ))}
          </View>
        )}
    </ScrollView>
  );
}
